import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  StoreState,
  AuthState as AuthStateType,
  Token,
  User,
  createInitialState,
  validateToken,
  validateUser
} from './schemas';
import { PERMISSION } from '../config/authConfig';

interface AuthStore extends StoreState {
  // Authentication actions
  setTokens: (tokens: Token) => void;
  setUser: (user: User) => void;
  setLoginHint: (loginHint: string) => void;
  setAuthenticating: (isLoading: boolean) => void;
  setInitialized: (isInitialized: boolean) => void;
  setAuthError: (error: string | undefined) => void;
  clearAuth: () => void;

  // Token validation helpers
  isTokenValid: () => boolean;
  isTokenExpiringSoon: (minutesThreshold?: number) => boolean;

  // UI actions
  setCurrentRoute: (route: string) => void;
  setUILoading: (isLoading: boolean) => void;
  addUIError: (error: string) => void;
  clearUIErrors: () => void;

  // MSAL integration methods (will be implemented by MSAL adapter)
  initializeAuth: (loginHint: string) => Promise<void>;
  refreshTokens: (scopes?: string[]) => Promise<string>;
  getValidToken: (scopes: string[]) => Promise<string>;
  logout: () => Promise<void>;
}

// Create the store
export const useAuthStore = create<AuthStore>()(
  devtools(
    (set, get) => ({
      // Initial state using Zod validation
      ...createInitialState(),

      // Authentication actions
      setTokens: (tokens) => {
        try {
          const validatedTokens = validateToken(tokens);
          set((state) => ({
            auth: {
              ...state.auth,
              tokens: validatedTokens,
              isAuthenticated: true,
              lastRefresh: Date.now(),
              error: undefined
            }
          }), false, 'setTokens');
        } catch (error) {
          console.error('Invalid tokens provided:', error);
          set((state) => ({
            auth: {
              ...state.auth,
              error: `Invalid token format: ${error instanceof Error ? error.message : String(error)}`
            }
          }), false, 'setTokens/error');
        }
      },

      setUser: (user) => {
        try {
          const validatedUser = validateUser(user);
          set((state) => ({
            auth: {
              ...state.auth,
              user: validatedUser,
              error: undefined
            }
          }), false, 'setUser');
        } catch (error) {
          console.error('Invalid user data provided:', error);
          set((state) => ({
            auth: {
              ...state.auth,
              error: `Invalid user data: ${error instanceof Error ? error.message : String(error)}`
            }
          }), false, 'setUser/error');
        }
      },

      setLoginHint: (loginHint) => {
        set((state) => ({
          auth: {
            ...state.auth,
            loginHint
          }
        }), false, 'setLoginHint');
      },

      setAuthenticating: (isLoading) => {
        set((state) => ({
          auth: {
            ...state.auth,
            isLoading
          }
        }), false, 'setAuthenticating');
      },

      setInitialized: (isInitialized) => {
        set((state) => ({
          auth: {
            ...state.auth,
            isInitialized
          }
        }), false, 'setInitialized');
      },

      setAuthError: (error) => {
        set((state) => ({
          auth: {
            ...state.auth,
            error,
            isLoading: false
          }
        }), false, 'setAuthError');
      },

      clearAuth: () => {
        set((state) => ({
          auth: {
            ...state.auth,
            isAuthenticated: false,
            tokens: undefined,
            user: undefined,
            lastRefresh: undefined,
            error: undefined,
            isLoading: false
          }
        }), false, 'clearAuth');
      },

      // Token validation helpers
      isTokenValid: () => {
        const { tokens } = get().auth;
        if (!tokens) return false;
        return Date.now() < tokens.expiresAt;
      },

      isTokenExpiringSoon: (minutesThreshold = 5) => {
        const { tokens } = get().auth;
        if (!tokens) return true;
        const thresholdMs = minutesThreshold * 60 * 1000;
        return Date.now() + thresholdMs >= tokens.expiresAt;
      },

      // UI actions
      setCurrentRoute: (route) => {
        set((state) => ({
          ui: {
            ...state.ui,
            currentRoute: route
          }
        }), false, 'setCurrentRoute');
      },

      setUILoading: (isLoading) => {
        set((state) => ({
          ui: {
            ...state.ui,
            isLoading
          }
        }), false, 'setUILoading');
      },

      addUIError: (error) => {
        set((state) => ({
          ui: {
            ...state.ui,
            errors: [...state.ui.errors, error]
          }
        }), false, 'addUIError');
      },

      clearUIErrors: () => {
        set((state) => ({
          ui: {
            ...state.ui,
            errors: []
          }
        }), false, 'clearUIErrors');
      },

      // MSAL integration placeholders (will be implemented by adapter)
      initializeAuth: async (loginHint: string) => {
        // This will be overridden by the MSAL adapter
        // console.warn('initializeAuth not implemented - use MSAL adapter');
      },

      refreshTokens: async (scopes = PERMISSION) => {
        // This will be overridden by the MSAL adapter
        // console.warn('refreshTokens not implemented - use MSAL adapter');
        return '';
      },

      getValidToken: async (scopes: string[]) => {
        // This will be overridden by the MSAL adapter
        // console.warn('getValidToken not implemented - use MSAL adapter');
        return '';
      },

      logout: async () => {
        // This will be overridden by the MSAL adapter
        // console.warn('logout not implemented - use MSAL adapter');
      }
    }),
    {
      name: 'auth-store',
    }
  )
);

// Selectors for common state access patterns
export const useAuthState = () => useAuthStore((state) => state.auth);
export const useUIState = () => useAuthStore((state) => state.ui);
export const useIsAuthenticated = () => useAuthStore((state) => state.auth.isAuthenticated);
export const useCurrentUser = () => useAuthStore((state) => state.auth.user);
export const useAuthError = () => useAuthStore((state) => state.auth.error);
export const useIsLoading = () => useAuthStore((state) => state.auth.isLoading);

// Action selectors
export const useAuthActions = () => {
  const store = useAuthStore();
  return {
    setTokens: store.setTokens,
    setUser: store.setUser,
    setLoginHint: store.setLoginHint,
    setAuthenticating: store.setAuthenticating,
    setInitialized: store.setInitialized,
    setAuthError: store.setAuthError,
    clearAuth: store.clearAuth,
    isTokenValid: store.isTokenValid,
    isTokenExpiringSoon: store.isTokenExpiringSoon
  };
}; 